import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { ModelPagination } from '@models-global/model-pagination';
import {
  Customer,
  ModelCustomer
} from '@module-customer/models/model-customer';
import { environment } from '@environment/environment';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpResp } from '@models-global/http-response';
import {
  IClaimUpload,
  IClaimsFileUploadLogParams
} from '@module-claims/models/claim-upload.interface';
import {
  REBATE_CLAIM_STATUS_CONFIGURATION,
  AUDIT_340B_CLAIM_STATUS_CONFIGURATION,
  ENUM_CLAIM_STATUS
} from '@module-claims/enums/enum-claim-status';
import {
  IClaimDetail,
  IClaimAuditStatusUpdateParams
} from '@module-claims/models/claim.interface';
import {
  CompositeFilterDescriptor,
  SortDescriptor
} from '@progress/kendo-data-query';
import { FilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import { ModelUser } from '@module-user/models/model-user';
import { IClaimsCustomer } from '@module-claims/models/claims-customer.interface';
import { ModelClaimsDocuments } from '@module-claims/models/claims-document.interface';
import { IClaimList } from '@module-claims/models/claim-list.interface';
import { IClaimListElement } from '@module-claims/models/claim-list-element.interface';
import { GRID_PAGE_SIZE } from '@models-global/constants';
import { createErrorObj } from '@utils-global/error-utils';
import {
  IClaimsSummary,
  IClaimsSummaryBatchCategoryStatGeneric,
  IClaimsSummaryBatchCategoryStatInsight,
  IClaimsSummaryBatchStatistics
} from '@module-claims/models/claims-summary.interface';
import { FileNamePipe } from '@module-shared/pipes/file-name/file-name.pipe';
import { CustomerServiceType } from '@module-customer/enums/customer-service-type.enum';
import {
  ClaimFileDownloadRequest,
  ClaimFileDownloadResponse
} from '@module-claims/models/claim-file-download.interface';
import { IClaimFileStatus } from '@module-claims/models/claim-file-status.interface';
import {
  ClaimsSummaryParams,
  ClaimsSummaryStatisticsParams,
  ClaimsSummaryBatchParams,
  ClaimsSummaryResponse,
  ClaimsSummaryStatisticsResponse,
  ClaimsGroupListResponseV2,
  ClaimsGroupListParamsV2,
  ClaimsGroupClaimsResponseV2,
  ClaimBatchUpdateParamsV2,
  ClaimQueueUpdateParams,
  AuditClaimFileSummaryParams,
  AuditClaimFileExportParams,
  AuditReportingStatusTotals,
  AuditReportingResolutionTrend,
  AuditReportingTop340BMetrics,
  AuditReportingTopNot340BMetrics,
  AuditReportingTopDisputedMetrics,
  AuditReportingTopDisputeResolutionMetrics,
  AuditClaimSearchParams,
  AuditClaimSearchResponse,
  AuditClaimSearchResult
} from '@module-claims/models/claims-http.interface';
import {
  IClaimFile340BAudit,
  IClaimTotals340BAudit,
  IClaimsGroupClaimV2,
  IClaimsGroupSummaryItemV2
} from '@module-claims/models/claims-group.interface';
import { PharmacyBenefitManager } from '@module-claims/models/pharmacy-benefit-manager.interface';
import { IClaimFileLOB } from '@module-claims/models/claim-file-lob.interface';
import {
  PharmacyDetails,
  ContractPharmacyDetails,
  ContractPharmacyMappingDetails,
  InhousePharmacyDetails,
  InhousePharmacyMappingDetails,
  InhousePharmacyLinkParams,
  ContractPharmacyLinkParams
} from '@module-claims/models/pharmacy.interface';
import { getISODateString } from '@utils-global/date-time';
import {
  CEAccountData,
  CEAssociation,
  CEAssociationManual,
  CEAssociationUnique,
  PrescriberCEAssociationAddParams,
  PrescriberCEAssociationRemoveParams,
  PrescriberDetails,
  PrescriberDetailsSimple
} from '@module-claims/models/prescriber.interface';
import {
  ClaimsCEsContactParams,
  ClaimsCEsGetParams,
  ClaimsCEsGetResponse
} from '@module-claims/models/claim-ce.interface';
import { transformCoveredEntityClaim } from '@module-covered-entity/models/ce-claim';
import {
  MFPClaim,
  MFPClaimsGetParams,
  MFPClaimsUpdateParams,
  transformMFPClaim
} from '@module-claims/models/mfp-claim.interface';
import { Values } from '@utils-global/type-values';

@Injectable()
export class ClaimsHttpService {
  readonly apiCustomerAll = `${environment.api}/customer/all?take=1000&sortBy=name&orderBy=asc`;
  readonly apiClaim = `${environment.api}/claim`;

  constructor(private http: HttpClient, private fileNamePipe: FileNamePipe) {}

  getCustomersList(
    filters?: (CompositeFilterDescriptor | FilterDescriptor)[]
  ): Observable<ModelPagination<ModelCustomer[]>> {
    let params = new HttpParams();

    if (filters) {
      let filterArr = (filters as { [key: string]: any })[
        'filters'
      ] as FilterDescriptor[];

      for (const item of filterArr) {
        const key = item.field as string;
        const value = { operator: item.operator, value: item.value };
        params = params.append(key, JSON.stringify(value));
      }
    }

    const opt = {
      headers: new HttpHeaders(),
      params
    };

    return this.http
      .get<HttpResp<ModelPagination<Customer[]>>>(this.apiCustomerAll, opt)
      .pipe(
        map((response) => {
          const models = response.data.data.map(
            (customer: Partial<Customer> | undefined) =>
              new ModelCustomer(customer)
          );

          return {
            data: models,
            total: models.length,
            skip: response.data.skip,
            take: response.data.take
          };
        })
      );
  }

  getPBMList(): Observable<PharmacyBenefitManager[]> {
    return this.http
      .get<HttpResp<PharmacyBenefitManager[]>>(`${environment.api}/list-pbm`)
      .pipe(map(({ data }) => data || []));
  }

  getLOBList(): Observable<IClaimFileLOB[]> {
    return this.http
      .get<HttpResp<IClaimFileLOB[]>>(`${environment.api}/list-lob`)
      .pipe(map(({ data }) => data || []));
  }

  getClaimsUploadList({
    skip,
    take,
    sort = [],
    filters = [],
    customerId,
    rebateCycle
  }: IClaimsFileUploadLogParams): Observable<ModelPagination<IClaimUpload[]>> {
    return this.http
      .post<{ count: number; data: IClaimUpload[] }>(
        `${environment.api}/uploadLog`,
        {
          pageSize: take,
          pageNumber: skip / take + 1,
          sortBy: sort[0]?.field ?? 'uploadDate',
          sortOrder: sort[0]?.dir ?? 'desc',
          filters,
          customerId: customerId ?? undefined,
          rebateCycleMonth: rebateCycle && rebateCycle?.getMonth() + 1,
          rebateCycleYear: rebateCycle && rebateCycle?.getFullYear()
        }
      )
      .pipe(
        map(({ count, data }) => ({
          skip,
          take,
          total: count,
          data
        }))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  reverseClaim(claimUploadId: string): Observable<any> {
    // TODO replace with BE endpoint
    return of(true);
  }

  getClaimsGroupsListV2({
    entityId,
    status,
    customerId,
    skip = 0,
    take = 1000,
    sort = []
  }: ClaimsGroupListParamsV2): Observable<
    ModelPagination<IClaimsGroupSummaryItemV2[]>
  > {
    const statusConfig = REBATE_CLAIM_STATUS_CONFIGURATION.get(
      status as Values<typeof ENUM_CLAIM_STATUS>
    );
    if (!statusConfig) {
      return throwError(
        createErrorObj('Error', `Claim status not configured (${status})`)
      );
    }

    // Paging
    const pageNum = skip / take + 1;
    const sortCol = sort[0]?.field;
    const sortDir = sort[0]?.dir;

    return this.http
      .post<ClaimsGroupListResponseV2>(`${environment.api}/claimssummary`, {
        entityid: entityId,
        option: statusConfig.category,
        customerId: customerId,
        sortBy: sortCol,
        orderBy: sortDir,
        pageNumber: pageNum,
        pageSize: take
      })
      .pipe(
        map(({ response: { count, totalCount, data } }) => ({
          skip,
          take,
          sort,
          total: count,
          data: data.length
            ? [
                ...totalCount.map((i) => ({
                  ...i,
                  ManufacturerName: 'All',
                  StatusCodes: 'All',
                  category: 'All'
                })),
                ...data
              ]
            : []
        })),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims groups data')
          );
        })
      );
  }

  getClaimsGroupClaimsListV2(
    params: ClaimsGroupListParamsV2
  ): Observable<ModelPagination<IClaimsGroupClaimV2[]>> {
    if (params.serviceType === CustomerServiceType.AUDIT_340B) {
      switch (params.status) {
        case ENUM_CLAIM_STATUS.AUDIT_CLIENT_REVIEW:
          return this.get340BAuditCustomerQueueClaims(params);
        case ENUM_CLAIM_STATUS.AUDIT_EXTERNAL_REVIEW:
          return this.get340BAuditExternalQueueClaims(params);
        case ENUM_CLAIM_STATUS.AUDIT_DASHBOARD:
          return this.get340BAuditAllClaims(params);
        default:
          return this.get340BAuditManualReviewClaims(params);
      }
    } else {
      return this.getRebateClaims(params);
    }
  }

  getRebateClaims({
    entityId,
    status,
    manufacturerIds,
    statusCodes,
    categories,
    customerId,
    skip = 0,
    take = GRID_PAGE_SIZE,
    sort = [],
    filters = {},
    compositeFilters = []
  }: ClaimsGroupListParamsV2): Observable<
    ModelPagination<IClaimsGroupClaimV2[]>
  > {
    const statusConfig = REBATE_CLAIM_STATUS_CONFIGURATION.get(
      status as Values<typeof ENUM_CLAIM_STATUS>
    );
    if (!statusConfig) {
      return throwError(
        createErrorObj('Error', `Claim status not configured (${status})`)
      );
    }

    // Paging
    const pageNum = skip / take + 1;
    const sortCol = sort[0]?.field;
    const sortDir = sort[0]?.dir;

    return this.http
      .post<ClaimsGroupClaimsResponseV2>(
        `${environment.api}/claims`,
        {
          entityid: entityId,
          option: statusConfig.category,
          manufacturerId: manufacturerIds,
          StatusCodes: statusCodes,
          category: categories,
          customerId: customerId,
          filters: compositeFilters,
          pageNumber: pageNum,
          pageSize: take,
          sortBy: sortCol,
          sortOrder: sortDir
        },
        {
          params: {
            ...filters
          }
        }
      )
      .pipe(
        map(({ response: { count, data } }) => ({
          skip,
          take,
          sort,
          total: count,
          data: data || []
        })),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  get340BAuditManualReviewClaims({
    entityId,
    status,
    fileId,
    skip = 0,
    take = GRID_PAGE_SIZE,
    sort = [],
    filters = {},
    compositeFilters = []
  }: ClaimsGroupListParamsV2): Observable<
    ModelPagination<IClaimsGroupClaimV2[]>
  > {
    const statusConfig = AUDIT_340B_CLAIM_STATUS_CONFIGURATION.get(
      status as Values<typeof ENUM_CLAIM_STATUS>
    );
    if (!statusConfig) {
      return throwError(
        createErrorObj('Error', `Claim status not configured (${status})`)
      );
    }

    const sortColOverrides: { [key: string]: string } = {
      'Date Filled': '[Date Filled]'
    };

    // Paging
    const pageNum = skip / take + 1;
    const sortCol = sort[0]?.field
      ? sortColOverrides[sort[0].field] || sort[0]?.field
      : undefined;
    const sortDir = sort[0]?.dir;

    return this.http
      .post<ClaimsGroupClaimsResponseV2>(
        `${environment.api}/audit-340b-claims`,
        {
          clientId: entityId,
          statusCategory: statusConfig.category,
          ActiveQueueName: statusConfig.queue,
          filters: compositeFilters,
          pageNumber: pageNum,
          pageSize: take,
          sortBy: sortCol,
          sortOrder: sortDir,
          fileId: fileId ? [fileId] : [],
          lobId: filters.lobId ?? undefined
        },
        {
          params: {
            ...filters
          }
        }
      )
      .pipe(
        map(({ response: { count, data } }) => ({
          skip,
          take,
          sort,
          total: count,
          data: data || []
        })),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  get340BAuditCustomerQueueClaims({
    entityId,
    status,
    fileId,
    skip = 0,
    take = GRID_PAGE_SIZE,
    sort = [],
    filters = {},
    compositeFilters = []
  }: ClaimsGroupListParamsV2): Observable<
    ModelPagination<IClaimsGroupClaimV2[]>
  > {
    const statusConfig = AUDIT_340B_CLAIM_STATUS_CONFIGURATION.get(
      status as Values<typeof ENUM_CLAIM_STATUS>
    );
    if (!statusConfig) {
      return throwError(
        createErrorObj('Error', `Claim status not configured (${status})`)
      );
    }

    const sortColOverrides: { [key: string]: string } = {
      entityType340b: 'CEID340B'
    };

    // Paging
    const pageNum = skip / take + 1;
    const sortCol = sort[0]?.field
      ? sortColOverrides[sort[0].field] || sort[0]?.field
      : undefined;
    const sortDir = sort[0]?.dir;

    return this.http
      .post<{ data: IClaimsGroupClaimV2[]; count: number }>(
        `${environment.api}/dispute-audit-list`,
        {
          ...filters,
          ClientId: entityId,
          ActiveQueue: statusConfig.queue,
          FileId: fileId ? [fileId] : [],
          lobId: filters.lobId ?? undefined,
          filters: compositeFilters,
          pageNumber: pageNum,
          pageSize: take,
          sortBy: sortCol,
          sortOrder: sortDir
        },
        {
          params: {
            ...filters
          }
        }
      )
      .pipe(
        map(({ data, count }) => ({
          skip,
          take,
          sort,
          total: count || 0,
          data: data || []
        })),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  get340BAuditExternalQueueClaims({
    entityId,
    status,
    fileId,
    skip = 0,
    take = GRID_PAGE_SIZE,
    sort = [],
    filters = {},
    compositeFilters = []
  }: ClaimsGroupListParamsV2): Observable<
    ModelPagination<IClaimsGroupClaimV2[]>
  > {
    const statusConfig = AUDIT_340B_CLAIM_STATUS_CONFIGURATION.get(
      status as Values<typeof ENUM_CLAIM_STATUS>
    );
    if (!statusConfig) {
      return throwError(
        createErrorObj('Error', `Claim status not configured (${status})`)
      );
    }

    const sortColOverrides: { [key: string]: string } = {
      entityType340b: 'CEID340B'
    };

    // Paging
    const pageNum = skip / take + 1;
    const sortCol = sort[0]?.field
      ? sortColOverrides[sort[0].field] || sort[0]?.field
      : undefined;
    const sortDir = sort[0]?.dir;

    return this.http
      .post<{ data: IClaimsGroupClaimV2[]; count: number }>(
        `${environment.api}/resolution-audit-list`,
        {
          ...filters,
          ClientId: entityId,
          ActiveQueue: statusConfig.queue,
          statusCategoryIds: statusConfig.statusIds ?? [],
          FileId: fileId ? [fileId] : [],
          filters: compositeFilters,
          pageNumber: pageNum,
          pageSize: take,
          sortBy: sortCol,
          sortOrder: sortDir
        },
        {
          params: {
            ...filters
          }
        }
      )
      .pipe(
        map(({ data, count }) => ({
          skip,
          take,
          sort,
          total: count || 0,
          data: data || []
        })),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  get340BAuditAllClaims({
    entityId,
    skip = 0,
    take = GRID_PAGE_SIZE,
    sort = [],
    filters = {},
    compositeFilters = []
  }: ClaimsGroupListParamsV2): Observable<
    ModelPagination<IClaimsGroupClaimV2[]>
  > {
    const sortColOverrides: { [key: string]: string } = {
      entityType340b: 'CEID340B'
    };

    // Paging
    const pageNum = skip / take + 1;
    const sortCol = sort[0]?.field
      ? sortColOverrides[sort[0].field] || sort[0]?.field
      : undefined;
    const sortDir = sort[0]?.dir;

    return this.http
      .post<{ data: IClaimsGroupClaimV2[]; count: number }>(
        `${environment.api}/audit-dashboard-claims-list`,
        {
          ...filters,
          ClientId: entityId,
          filters: compositeFilters,
          pageNumber: pageNum,
          pageSize: take,
          sortBy: sortCol,
          sortOrder: sortDir
        },
        {
          params: {
            ...filters
          }
        }
      )
      .pipe(
        map(({ data, count }) => ({
          skip,
          take,
          sort,
          total: count || 0,
          data: data || []
        })),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  updateClaimsStatusV2(params: ClaimBatchUpdateParamsV2): Observable<any> {
    if (params.serviceType === CustomerServiceType.AUDIT_340B) {
      switch (params.status) {
        case ENUM_CLAIM_STATUS.AUDIT_CLIENT_REVIEW:
        case ENUM_CLAIM_STATUS.AUDIT_EXTERNAL_REVIEW:
          return this.update340BAuditClaimsDisputeStatus(params);
        default:
          return this.update340BAuditClaimsStatus(params);
      }
    } else {
      return this.updateRebateClaimsStatus(params);
    }
  }

  updateRebateClaimsStatus({
    entityId,
    status,
    updateStatus,
    updateComment,
    invoice,
    manufacturerIds,
    rxpManagedManufacturerIds,
    statusCodes,
    categories,
    customerId,
    intakeIds,
    excludeIntakeIds,
    rxpManagedIntakeIds,
    hostComments,
    manufacturerComments,
    customerComments,
    adjustedRebateAmount,
    adjustedAdminAmount,
    adjustedPPAmount,
    filters = {},
    compositeFilters = []
  }: ClaimBatchUpdateParamsV2): Observable<any> {
    let currentStatus = status;

    if (typeof status !== 'number') {
      const statusConfig = REBATE_CLAIM_STATUS_CONFIGURATION.get(status);
      if (!statusConfig) {
        return throwError(
          createErrorObj('Error', `Unmapped claim status group (${status})`)
        );
      }
      currentStatus = statusConfig.category;
    }

    return this.http
      .post(
        `${environment.api}/claims-status-update`,
        {
          entityid: entityId,
          option: currentStatus,
          manufacturerId: manufacturerIds ?? [],
          rxpManagedManufacturerId: rxpManagedManufacturerIds ?? [],
          StatusCodes: statusCodes,
          category: categories,
          customerId: customerId,
          IntakeIdArray: intakeIds,
          rxpManagedIntakeId: rxpManagedIntakeIds ?? [],
          excludeIntakeIds: excludeIntakeIds ?? [],
          updateStatus,
          updateComment,
          invoice,
          hostComments,
          manufacturerComments,
          customerComments,
          adjustedRebateAmount,
          adjustedAdminAmount,
          adjustedPPAmount,
          filters: compositeFilters
        },
        {
          params: {
            ...filters
          }
        }
      )
      .pipe(
        catchError(() => {
          return throwError(createErrorObj('Error', 'Failed to update claims'));
        })
      );
  }

  update340BAuditClaimsStatus({
    entityId,
    status,
    updateStatus,
    intakeIds,
    excludeIntakeIds,
    fileId,
    filters = {},
    compositeFilters = []
  }: ClaimBatchUpdateParamsV2): Observable<any> {
    const statusConfig = AUDIT_340B_CLAIM_STATUS_CONFIGURATION.get(
      status as Values<typeof ENUM_CLAIM_STATUS>
    );
    if (!statusConfig) {
      return throwError(
        createErrorObj('Error', `Unmapped claim status group (${status})`)
      );
    }

    return this.http
      .post(
        `${environment.api}/audit-340b-claims-status-update`,
        {
          clientId: entityId,
          ActiveQueue: statusConfig.queue,
          IntakeIds: intakeIds ?? [],
          excludeIntakeIds: excludeIntakeIds ?? [],
          updateFrom: statusConfig.category,
          updateTo: updateStatus,
          filters: compositeFilters,
          fileId: fileId ? [fileId] : [],
          lobId: filters.lobId ?? undefined
        },
        {
          params: {
            ...filters
          }
        }
      )
      .pipe(
        catchError(() => {
          return throwError(createErrorObj('Error', 'Failed to update claims'));
        })
      );
  }

  update340BAuditClaimsDisputeStatus({
    entityId,
    status,
    updateStatus,
    intakeIds,
    excludeIntakeIds,
    fileId,
    filters = {},
    compositeFilters = []
  }: ClaimBatchUpdateParamsV2): Observable<any> {
    const statusConfig = AUDIT_340B_CLAIM_STATUS_CONFIGURATION.get(
      status as Values<typeof ENUM_CLAIM_STATUS>
    );
    if (!statusConfig) {
      return throwError(
        createErrorObj('Error', `Unmapped claim status group (${status})`)
      );
    }

    return this.http
      .post(
        `${environment.api}/dispute-status-update`,
        {
          ...filters,
          clientId: entityId,
          ActiveQueue: statusConfig.queue,
          intakeIds: intakeIds ?? [],
          excludeIntakeIds: excludeIntakeIds ?? [],
          updateTo: updateStatus,
          fileId: fileId ? [fileId] : [],
          filters: compositeFilters
        },
        {
          params: {
            ...filters
          }
        }
      )
      .pipe(
        catchError(() => {
          return throwError(createErrorObj('Error', 'Failed to update claims'));
        })
      );
  }

  update340BAuditClaimsQueue({
    entityId,
    status,
    updateQueue,
    intakeIds,
    fileIds,
    lobId
  }: ClaimQueueUpdateParams): Observable<any> {
    const statusConfig = AUDIT_340B_CLAIM_STATUS_CONFIGURATION.get(status);
    if (!statusConfig) {
      return throwError(
        createErrorObj('Error', `Unmapped claim status group (${status})`)
      );
    }

    return this.http
      .post(`${environment.api}/update-audit-queue`, {
        clientId: entityId,
        IntakeIds: intakeIds ?? [],
        fileId: fileIds ?? [],
        lobId: lobId ?? undefined,
        currentQueue: statusConfig.queue,
        updateQueue
      })
      .pipe(
        catchError(() => {
          return throwError(createErrorObj('Error', 'Failed to update claims'));
        })
      );
  }

  get340BAuditClaimsFiles({
    status,
    customerId
  }: AuditClaimFileSummaryParams): Observable<IClaimFile340BAudit[]> {
    switch (status) {
      case ENUM_CLAIM_STATUS.AUDIT_340B:
      case ENUM_CLAIM_STATUS.AUDIT_NOT_340B:
      case ENUM_CLAIM_STATUS.AUDIT_NOT_EVALUATED:
        return this.get340BAuditHostQueueFiles(customerId);
      case ENUM_CLAIM_STATUS.AUDIT_CLIENT_REVIEW:
        return this.get340BAuditCustomerQueueFiles(customerId);
      case ENUM_CLAIM_STATUS.AUDIT_EXTERNAL_REVIEW:
        return this.get340BAuditExternalQueueFiles(customerId);
      case ENUM_CLAIM_STATUS.AUDIT_DASHBOARD:
        return this.get340BAuditDashboardClaimFiles(customerId);
      default:
        return of([]);
    }
  }

  get340BAuditHostQueueFiles(
    customerId: string
  ): Observable<IClaimFile340BAudit[]> {
    return this.http
      .post<HttpResp<IClaimFile340BAudit[]>>(
        `${environment.api}/manual-file-list`,
        { clientId: customerId }
      )
      .pipe(
        map((res) => res.data || []),
        catchError(() =>
          throwError(createErrorObj('Error', 'Failed to retrieve claim files'))
        )
      );
  }

  get340BAuditCustomerQueueFiles(
    customerId: string
  ): Observable<IClaimFile340BAudit[]> {
    return this.http
      .post<HttpResp<IClaimFile340BAudit[]>>(
        `${environment.api}/dispute-audit-list-summary`,
        { ClientId: customerId }
      )
      .pipe(
        map((res) => res.data || []),
        catchError(() =>
          throwError(createErrorObj('Error', 'Failed to retrieve claim files'))
        )
      );
  }

  get340BAuditExternalQueueFiles(
    customerId: string
  ): Observable<IClaimFile340BAudit[]> {
    return this.http
      .get<HttpResp<IClaimFile340BAudit[]>>(
        `${environment.api}/dispute-audit-external-files-list/${customerId}`
      )
      .pipe(
        map((res) => res.data || []),
        catchError(() =>
          throwError(createErrorObj('Error', 'Failed to retrieve claim files'))
        )
      );
  }

  get340BAuditCustomerReviewedFiles(
    customerId: string
  ): Observable<IClaimFile340BAudit[]> {
    return this.http
      .get<HttpResp<IClaimFile340BAudit[]>>(
        `${environment.api}/dispute-audit-processed-files-list/${customerId}`
      )
      .pipe(
        map(({ data }) => data || []),
        catchError(() =>
          throwError(
            createErrorObj('Error', 'Failed to retrieve reviewed claim files')
          )
        )
      );
  }

  get340BAuditDashboardClaimFiles(
    customerId: string
  ): Observable<IClaimFile340BAudit[]> {
    return this.http
      .get<HttpResp<IClaimFile340BAudit[]>>(
        `${environment.api}/audit-dashboard-claims-files/${customerId}`
      )
      .pipe(
        map(({ data }) => data || []),
        catchError(() =>
          throwError(
            createErrorObj('Error', 'Failed to retrieve dashboard claim files')
          )
        )
      );
  }

  get340BAuditCustomerReviewClaimsTotals(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<IClaimTotals340BAudit> {
    return this.http
      .get<IClaimTotals340BAudit>(
        `${environment.api}/dispute-audit-count/${customerId}`,
        {
          params: filters ?? {}
        }
      )
      .pipe(
        catchError(() =>
          throwError(createErrorObj('Error', 'Failed to retrieve claim totals'))
        )
      );
  }

  exportAuditClaimFiles({
    customerId,
    fileId,
    pbmId,
    category,
    rebateCycleMonth,
    rebateCycleYear
  }: AuditClaimFileExportParams): Observable<any> {
    return this.http.post(`${environment.api}/external-queue-batch-export`, {
      ClientId: customerId,
      fileId: fileId ?? undefined,
      pbmId: pbmId ?? undefined,
      category: category ?? undefined,
      rebateCycleMonth: rebateCycleMonth ?? undefined,
      rebateCycleYear: rebateCycleYear ?? undefined
    });
  }

  exportAuditClaims({
    entityId,
    filters = {},
    compositeFilters = []
  }: ClaimsGroupListParamsV2): Observable<any> {
    return this.http
      .post<{ message: string }>(
        `${environment.api}/dashboard-csv-download`,
        {
          ...filters,
          ClientId: entityId,
          filters: compositeFilters
        },
        {
          params: {
            ...filters
          }
        }
      )
      .pipe(
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  reevaluateClaims(
    customerId: string,
    serviceType: CustomerServiceType
  ): Observable<any> {
    return this.http
      .post(`${environment.api}/manual-review/resubmit-file`, {
        customerId,
        customerType: serviceType
      })
      .pipe(
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to initiate claims re-evaluation')
          );
        })
      );
  }

  getAuditReportingStatusTotals(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<AuditReportingStatusTotals> {
    return this.http
      .post<HttpResp<AuditReportingStatusTotals>>(
        `${environment.api}/audit-reporting/status-totals`,
        {
          ...filters,
          ClientId: customerId
        }
      )
      .pipe(
        map(({ data }) => data),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  getAuditReportingResolutionTrends(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<AuditReportingResolutionTrend[]> {
    return this.http
      .post<HttpResp<AuditReportingResolutionTrend[]>>(
        `${environment.api}/audit-reporting/resolution-trends`,
        {
          ...filters,
          ClientId: customerId
        }
      )
      .pipe(
        map(({ data }) => data),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  getAuditReportingTop340B(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<AuditReportingTop340BMetrics> {
    return this.http
      .post<HttpResp<AuditReportingTop340BMetrics>>(
        `${environment.api}/audit-reporting/top-340b`,
        {
          ...filters,
          ClientId: customerId
        }
      )
      .pipe(
        map(({ data }) => data),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  getAuditReportingTopNot340B(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<AuditReportingTopNot340BMetrics> {
    return this.http
      .post<HttpResp<AuditReportingTopNot340BMetrics>>(
        `${environment.api}/audit-reporting/top-not-340b`,
        {
          ...filters,
          ClientId: customerId
        }
      )
      .pipe(
        map(({ data }) => data),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  getAuditReportingTopDisputed(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<AuditReportingTopDisputedMetrics> {
    return this.http
      .post<HttpResp<AuditReportingTopDisputedMetrics>>(
        `${environment.api}/audit-reporting/top-disputed`,
        {
          ...filters,
          ClientId: customerId
        }
      )
      .pipe(
        map(({ data }) => data),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  getAuditReportingTopDisputeResolution(
    customerId: string,
    filters?: { [key: string]: string | number }
  ): Observable<AuditReportingTopDisputeResolutionMetrics> {
    return this.http
      .post<HttpResp<AuditReportingTopDisputeResolutionMetrics>>(
        `${environment.api}/audit-reporting/top-dispute-resolution`,
        {
          ...filters,
          ClientId: customerId
        }
      )
      .pipe(
        map(({ data }) => data),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claims data')
          );
        })
      );
  }

  uploadClaimIntoClaimGroup(
    code: string,
    claimsDocuments: ModelClaimsDocuments
  ): Observable<boolean> {
    return this.http
      .post<HttpResp<boolean>>(
        `${this.apiClaim}/customer/${code}/upload`,
        claimsDocuments.serializeToFormData()
      )
      .pipe(
        map((res) => res.data),
        catchError((e: HttpErrorResponse) => {
          return throwError(createErrorObj('Upload error', e.error?.error));
        })
      );
  }

  getClaimsList(
    userId: string,
    fileId: string,
    skip: number,
    take: number,
    entityId = '%20',
    statusCode = '%20',
    sort: SortDescriptor[] = [],
    serviceType = CustomerServiceType.REBATE_OPERATIONS
  ): Observable<ModelPagination<IClaimListElement[]>> {
    const pageNum = skip / take + 1;
    const sortCol = sort[0]?.field || '%20';
    const sortType = sort[0]?.dir || '%20';

    const filteredClaims = this.http
      .get<IClaimList>(
        `${environment.api}/claimfiledetails/${fileId}/${userId}/${entityId}/${statusCode}/${pageNum}/${take}/${sortCol}/${sortType}`
      )
      .pipe(
        map((res) => {
          const categoryMap: Map<string, string> =
            serviceType === CustomerServiceType.AUDIT_340B
              ? new Map([
                  ['A', '340B'],
                  ['R', 'Not 340B'],
                  ['NR', 'Not Evaluated']
                ])
              : new Map([
                  ['A', 'Approved'],
                  ['R', 'Rejected'],
                  ['NR', 'Rejected Non Rebateable']
                ]);
          const data = res.data || [];
          const count = res.count || [];
          const summary = (res.summary || []).map((s) => ({
            ...s,
            Category: categoryMap.get(s.CategoryCode) || s.Category
          }));

          return {
            skip,
            take,
            data,
            summary,
            total: count[0]?.count || data.length
          };
        })
      );

    return filteredClaims;
  }

  getClaimsCustomersList(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    skip = 0,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    take = 10,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    user?: ModelUser,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sort?: SortDescriptor[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    filter?: CompositeFilterDescriptor
  ): Observable<IClaimsCustomer> {
    return this.http.get<IClaimsCustomer>(
      `${environment.api}/clientClaimLog/${user!.id}`
    );
  }

  getClaimDetails(
    userId: string,
    claimIntakeId: string
  ): Observable<IClaimDetail> {
    const userIdEncoded = encodeURIComponent(userId);
    const intakeIdEncoded = encodeURIComponent(claimIntakeId);

    return this.http
      .get<{ data: IClaimDetail[] }>(
        `${environment.api}/claimqueueitem/${userIdEncoded}/${intakeIdEncoded}`
      )
      .pipe(
        map(({ data }) => {
          const claimData = (data || [])[0];

          if (claimData) {
            if (
              claimData.InvoiceId === '00000000-0000-0000-0000-000000000000'
            ) {
              claimData.InvoiceId = null;
            }

            if (claimData.tpaClaimData) {
              claimData.tpaClaimData = transformCoveredEntityClaim(
                claimData.tpaClaimData
              );
            }

            return claimData;
          } else {
            throw new Error('No claim data');
          }
        }),
        catchError(() => {
          return throwError(
            createErrorObj('Error', 'Failed to retrieve claim data')
          );
        })
      );
  }

  update340BAuditSingleClaim({
    intakeId,
    status,
    reasonText
  }: IClaimAuditStatusUpdateParams): Observable<any> {
    return this.http.post(
      `${environment.api}/audit-340b-claims/manual-review/${intakeId}/update`,
      {
        status,
        reasonText
      }
    );
  }

  complete340BAuditManualReviewSingleClaim(intakeId: string): Observable<any> {
    return this.http.post(
      `${environment.api}/audit-340b-claims/manual-review/${intakeId}/complete`,
      {}
    );
  }

  getClaimsSummary({
    userId,
    entityId,
    skip = 0,
    take = GRID_PAGE_SIZE,
    sort = []
  }: ClaimsSummaryParams): Observable<ModelPagination<IClaimsSummary[]>> {
    const pageNum = skip / take + 1 || 1;
    const pageSize = take || '%20';
    const sortCol = sort[0]?.field || '%20';
    const sortDir = sort[0]?.dir || '%20';

    return this.http
      .get<ClaimsSummaryResponse>(
        `${environment.api}/batchsummary/${userId}/${entityId}/${pageNum}/${pageSize}/${sortCol}/${sortDir}`
      )
      .pipe(
        map(({ count, data }) => {
          const total = (count || [])[0]?.count || 0;
          return {
            skip,
            total,
            take: take || total,
            data: (data || []).map((d) => ({
              ...d,
              BatchName: this.fileNamePipe.transform(d.BatchName)
            }))
          };
        }),
        catchError(() =>
          throwError(
            createErrorObj('Error', 'Failed to retrieve claims summary')
          )
        )
      );
  }

  getClaimsSummaryBatchStatistics({
    userId,
    entityId,
    batchId,
    level,
    skip = 0,
    take = GRID_PAGE_SIZE,
    sort = []
  }: ClaimsSummaryStatisticsParams): Observable<
    ModelPagination<IClaimsSummaryBatchStatistics>
  > {
    const pageNum = skip / take + 1 || 1;
    const pageSize = take || '%20';
    const sortDir = sort[0]?.dir || '%20';

    let sortCol: string;
    if (sort[0]?.field === 'Category') {
      sortCol = 'DrugName';
    } else {
      sortCol = sort[0]?.field || '%20';
    }

    return this.http
      .get<ClaimsSummaryStatisticsResponse>(
        `${environment.api}/batchsummarystatistics/${userId}/${entityId}/${batchId}/${level}/${pageNum}/${pageSize}/${sortCol}/${sortDir}`
      )
      .pipe(
        map(({ count, summary, data }) => {
          const total = (count || [])[0]?.count || 0;
          return {
            skip,
            total,
            take: take || total,
            data: {
              pharmacyData: (summary || [])[0]?.pharmacies,
              prescriberData: (summary || [])[0]?.prescribers,
              totalClaims: (count || [])[0]?.count_claims || 0,
              data: (data || []).map((d) => ({
                ...d,
                Category: d.DrugName,
                TotalRebate: this.calculateTotalValue(d.Rebate, d.Admin, d.PP)
              })),
              insights: {
                topRejectionReasons:
                  (summary || [])[0]?.status_codes?.map((i) =>
                    this.transformStatisticsInsight(i)
                  ) || [],
                topRejectionPharmacies:
                  (summary || [])[0]?.pharmacies?.map((i) =>
                    this.transformStatisticsInsight(i)
                  ) || [],
                topRejectionPhysicians:
                  (summary || [])[0]?.prescribers?.map((i) =>
                    this.transformStatisticsInsight(i)
                  ) || []
              }
            }
          };
        }),
        catchError(() =>
          throwError(
            createErrorObj(
              'Error',
              'Failed to retrieve claims summary batch statistics'
            )
          )
        )
      );
  }

  private transformStatisticsInsight(
    insight: IClaimsSummaryBatchCategoryStatInsight
  ): IClaimsSummaryBatchCategoryStatGeneric {
    return {
      Category:
        insight.StatusCode || insight.PharmacyId || insight.PrescriberId,
      TotalClaims: insight.ClaimCount,
      TotalWAC: insight.TotalWAC,
      TotalRebate: this.calculateTotalValue(
        insight.TotalRebateValue,
        insight.TotalAdminValue,
        insight.TotalPP
      ),
      Rebate: insight.TotalRebateValue,
      Admin: insight.TotalAdminValue,
      PP: insight.TotalPP
    };
  }

  private calculateTotalValue(
    totalRebate?: number,
    totalAdmin?: number,
    totalPP?: number
  ): number {
    return (totalRebate || 0) + (totalAdmin || 0) + (totalPP || 0);
  }

  downloadClaimsSummaryBatchClaims({
    userId,
    entityId,
    batchId,
    level
  }: ClaimsSummaryBatchParams): Observable<any> {
    return this.http.post(`${environment.api}/batch-export-start`, {
      userId,
      entityId,
      batchId,
      level
    });
  }

  downloadClaimFile({
    customerId,
    path
  }: ClaimFileDownloadRequest): Observable<ClaimFileDownloadResponse> {
    return this.http
      .get<HttpResp<ClaimFileDownloadResponse>>(
        `${this.apiClaim}/file-download-link`,
        {
          params: {
            customerId,
            path
          }
        }
      )
      .pipe(
        map(({ data }) => data),
        tap((file) => {
          if (file.link) {
            const link = document.createElement('a');
            link.style.display = 'none';
            link.href = file.link;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        })
      );
  }

  deleteClaimFile(fileId: string): Observable<any> {
    return this.http.delete<HttpResp<any>>(
      `${environment.api}/claimfile/${fileId}`
    );
  }

  getClaimFileStatus(fileId: string): Observable<IClaimFileStatus> {
    return this.http.get<IClaimFileStatus>(
      `${environment.api}/claimfile/${fileId}/has-invoiced-claims`
    );
  }

  getPharmacyDetails(npi: string): Observable<PharmacyDetails | undefined> {
    return this.http
      .get<HttpResp<PharmacyDetails>>(
        `${environment.api}/manual-review/get-pharmacy-details/${npi}`
      )
      .pipe(
        map(({ data }) => ({
          ...data,
          pharmacyDetails: {
            ...data.pharmacyDetails,
            npi,
            ncpdpqInfo: data.pharmacyDetails.ncpdpqInfo && {
              ...data.pharmacyDetails.ncpdpqInfo,
              store_open_date:
                data.pharmacyDetails.ncpdpqInfo.store_open_date &&
                getISODateString(
                  data.pharmacyDetails.ncpdpqInfo.store_open_date
                ),
              store_closure_date:
                data.pharmacyDetails.ncpdpqInfo.store_closure_date &&
                getISODateString(
                  data.pharmacyDetails.ncpdpqInfo.store_closure_date
                )
            }
          }
        })),
        catchError((e) => {
          if (e.status === 404) {
            return of(undefined);
          }
          return throwError(e);
        })
      );
  }

  getContractPharmacyDetails(
    hrsaPharmacyId: string
  ): Observable<ContractPharmacyDetails | undefined> {
    return this.http
      .post<HttpResp<ContractPharmacyDetails>>(
        `${environment.api}/manual-review/get-inhouse-contract-details`,
        { hrsaPharmacyId }
      )
      .pipe(
        map(({ data }) => data),
        catchError((e) => {
          if (e.status === 404) {
            return of(undefined);
          }
          return throwError(e);
        })
      );
  }

  linkContractPharmacy({
    npi,
    hrsaPharmacyId
  }: ContractPharmacyLinkParams): Observable<any> {
    return this.http
      .post(`${environment.api}/manual-review/update-pharmacy-details`, {
        npiPharmacy: npi,
        hrsaPharmacyId
      })
      .pipe(
        catchError(() =>
          throwError(createErrorObj('Error', 'Failed to link HRSA pharmacy'))
        )
      );
  }

  unlinkContractPharmacy(
    npi: string,
    contractPharmacy: ContractPharmacyMappingDetails
  ): Observable<any> {
    return this.http
      .post(`${environment.api}/manual-review/unlink-pharmacy`, {
        npiPharmacy: npi,
        hrsaPharmacyId: contractPharmacy.hrsaPharmacyId
      })
      .pipe(
        catchError(() =>
          throwError(createErrorObj('Error', 'Failed to unlink HRSA pharmacy'))
        )
      );
  }

  getInhousePharmacyDetails(
    id340b: string
  ): Observable<InhousePharmacyDetails | undefined> {
    return this.http
      .post<HttpResp<InhousePharmacyDetails>>(
        `${environment.api}/manual-review/get-inhouse-contract-details`,
        { id340b }
      )
      .pipe(
        map(({ data }) => ({
          ...data,
          ce_start_date: data.ce_start_date
            ? getISODateString(data.ce_start_date)
            : undefined,
          ce_termination_date: data.ce_termination_date
            ? getISODateString(data.ce_termination_date)
            : undefined,
          certified_decertified_date: data.certified_decertified_date
            ? getISODateString(data.certified_decertified_date)
            : undefined
        })),
        catchError((e) => {
          if (e.status === 404) {
            return of(undefined);
          }
          return throwError(e);
        })
      );
  }

  linkInhousePharmacy({
    npi,
    id340b,
    organization,
    _row_hashtag_address
  }: InhousePharmacyLinkParams): Observable<any> {
    return this.http
      .post(`${environment.api}/manual-review/update-pharmacy-details`, {
        npiPharmacy: npi,
        id340bObject: {
          id340b,
          organization,
          _row_hashtag_address
        }
      })
      .pipe(
        catchError(() =>
          throwError(
            createErrorObj('Error', 'Failed to link in-house pharmacy')
          )
        )
      );
  }

  unlinkInhousePharmacy(
    npi: string,
    inhousePharmacy: InhousePharmacyMappingDetails
  ): Observable<any> {
    return this.http
      .post(`${environment.api}/manual-review/unlink-pharmacy`, {
        npiPharmacy: npi,
        id340bObject: inhousePharmacy
      })
      .pipe(
        catchError(() =>
          throwError(
            createErrorObj('Error', 'Failed to unlink in-house pharmacy')
          )
        )
      );
  }

  private _transformCEAssociation(ce: CEAssociation): CEAssociation {
    return {
      ...ce,
      start_date_of_340B_association: ce.start_date_of_340B_association
        ? getISODateString(ce.start_date_of_340B_association)
        : undefined,
      end_date_of_340B_association: ce.end_date_of_340B_association
        ? getISODateString(ce.end_date_of_340B_association)
        : undefined
    };
  }

  private _transformCEAssociationUnique(
    ce: CEAssociationUnique
  ): CEAssociationUnique {
    return {
      ...ce,
      certified_decertified_date: ce.certified_decertified_date
        ? getISODateString(ce.certified_decertified_date)
        : undefined,
      ce_start_date: ce.ce_start_date
        ? getISODateString(ce.ce_start_date)
        : undefined,
      ce_termination_date: ce.ce_termination_date
        ? getISODateString(ce.ce_termination_date)
        : undefined
    };
  }

  private _transformManualCEAssociation(
    ce: CEAssociationManual
  ): CEAssociation {
    return {
      id: ce.id,
      associated_340B_CE_ID: ce.associatedCEID,
      associated_340B_parent_CE_ID: ce.associatedParentId,
      confidence_340b: ce.confidence340B,
      start_date_of_340B_association: ce.startDate
        ? getISODateString(ce.startDate)
        : undefined,
      end_date_of_340B_association: ce.endDate
        ? getISODateString(ce.endDate)
        : undefined
    };
  }

  private _transformCEAccountData(ce: CEAccountData): CEAccountData {
    return {
      ...ce,
      start_date: ce.start_date ? getISODateString(ce.start_date) : undefined,
      end_date: ce.end_date ? getISODateString(ce.end_date) : undefined
    };
  }

  private _transformPrescriberDetails(
    prescriber: PrescriberDetails
  ): PrescriberDetailsSimple {
    const name: string =
      prescriber.prescriberDetails?.ProviderNames?.ProviderName?.reduce(
        (longestName, currentName) => {
          const name = currentName.Name?.Full ?? '';
          return name.length > longestName.length ? name : longestName;
        },
        ''
      ) ?? '';

    return {
      name,
      npi: prescriber.provider_npi,
      locations:
        prescriber.prescriberDetails?.Locations?.Location?.map((location) => ({
          address: location.Address,
          matchingCEs:
            location.Address?.matchingCEs?.map((ce) =>
              this._transformCEAssociation(ce)
            ) ?? []
        })) ?? [],
      affiliations:
        prescriber.prescriberDetails?.Affiliations?.Affiliation?.map((a1) => ({
          name: a1.OrganizationName ?? '',
          affiliationType: a1.AffiliationType ?? '',
          address: a1.Address,
          matchingCEsByLN:
            a1.matchingCEsByLN?.map((ce) => this._transformCEAssociation(ce)) ??
            [],
          matchingCEsWithLocationAddress:
            a1.matchingCEsWithLocationAddress?.map((ce) =>
              this._transformCEAssociation(ce)
            ) ?? [],
          affiliations:
            a1.Affiliations?.Affiliations?.map((a2) => ({
              name: a2.OrganizationName ?? '',
              affiliationType: a2.AffiliationType ?? '',
              skipped: !!a2.skipped,
              address: a2.Address,
              matchingCEsWithAffiliationAddress:
                a2.matchingCEsWithAffiliationAddress?.map((ce) =>
                  this._transformCEAssociation(ce)
                ) ?? [],
              affiliations:
                a2.Affiliations?.Affiliations?.map((a3) => ({
                  name: a3.OrganizationName ?? '',
                  affiliationType: a3.AffiliationType ?? '',
                  skipped: !!a3.skipped,
                  address: a3.Address
                })) ?? []
            })) ?? []
        })) ?? [],
      affiliatedCE: (prescriber.list_of_address_unique ?? []).map((ce) =>
        this._transformCEAssociationUnique(ce)
      ),
      manualAddedCE: (prescriber.manual_added_ce ?? []).map((ce) =>
        this._transformCEAssociation(ce)
      ),
      manualRemovedCE: (prescriber.manual_removed_ce ?? []).map((ce) =>
        this._transformManualCEAssociation(ce)
      ),
      ceData: (prescriber.ce_data ?? []).map((ce) =>
        this._transformCEAccountData(ce)
      )
    };
  }

  getPrescriberDetails(
    npi: string
  ): Observable<PrescriberDetailsSimple | undefined> {
    return this.http
      .get<PrescriberDetails>(
        `${environment.apiServicesUrl}/prescriberDetails/${npi}`
      )
      .pipe(
        map((prescriber) => this._transformPrescriberDetails(prescriber)),
        catchError((e) => {
          if (e.status === 404) {
            return of(undefined);
          }
          return throwError(
            createErrorObj('Error', 'Failed to retrieve prescriber details')
          );
        })
      );
  }

  addPrescriberCEAssociation({
    action,
    npi,
    ceId,
    parentCEId,
    confidence,
    startDate,
    endDate
  }: PrescriberCEAssociationAddParams): Observable<any> {
    return this.http
      .post(`${environment.apiServicesUrl}/manual-update/prescriber-update`, {
        associationAction: action,
        prescriberNPI: npi,
        associatedCEID: ceId,
        associatedParentId: parentCEId ?? undefined,
        confidence340B: confidence ?? undefined,
        startDate: startDate
          ? `${getISODateString(startDate)}T00:00:00Z`
          : undefined,
        endDate: endDate ? `${getISODateString(endDate)}T00:00:00Z` : undefined
      })
      .pipe(
        catchError(() =>
          throwError(
            createErrorObj('Error', 'Failed to add covered entity association')
          )
        )
      );
  }

  removePrescriberCEAssociation({
    action,
    npi,
    associationId
  }: PrescriberCEAssociationRemoveParams): Observable<any> {
    return this.http
      .post(
        `${environment.apiServicesUrl}/manual-update/remove-prescriber-association`,
        {
          associationAction: action,
          prescriberNPI: npi,
          associationId
        }
      )
      .pipe(
        catchError(() =>
          throwError(
            createErrorObj(
              'Error',
              'Failed to remove covered entity association'
            )
          )
        )
      );
  }

  search340BAuditClaim({
    dateOfService,
    serviceProviderIdentifierQualifier,
    serviceProviderIdentifier,
    prescriptionServiceReferenceNumber,
    fillNumber,
    ndc
  }: AuditClaimSearchParams): Observable<AuditClaimSearchResult> {
    if (serviceProviderIdentifierQualifier !== 'npi') {
      throw new Error('Service Provider Identifier Qualifier not recognized');
    }

    return this.http
      .post<AuditClaimSearchResponse>(`${environment.api}/match-340b`, {
        headers: {
          transactionId: `TUN${new Date().getTime()}`
        },
        data: {
          pharmacy_claim: {
            pharmacy_npi: serviceProviderIdentifier,
            prescription_number: prescriptionServiceReferenceNumber,
            fill_number: fillNumber,
            date_of_service: dateOfService,
            ndc
          }
        }
      })
      .pipe(
        map(({ data }) => ({
          isFound: !!data.pharmacy_claim?.claim_found,
          ceId: data.pharmacy_claim?.ce_id,
          status: data.pharmacy_claim?.claim_status
            ? ['replenished', 'invoiced', 'eligible'].includes(
                data.pharmacy_claim.claim_status.toLowerCase()
              )
              ? '340B'
              : 'Not 340B'
            : undefined
        }))
      );
  }

  get340BAuditClaimsCEs({
    intakeIds
  }: ClaimsCEsGetParams): Observable<ClaimsCEsGetResponse> {
    return this.http
      .post<HttpResp<ClaimsCEsGetResponse>>(
        `${environment.api}/audit-340b-claims/ce-association`,
        { intakeIds }
      )
      .pipe(map(({ data }) => data));
  }

  contact340BAuditClaimsCEs({
    ceList
  }: ClaimsCEsContactParams): Observable<any> {
    return this.http.post(`${environment.api}/audit-340b-claims/contact-ce`, {
      ceList
    });
  }

  getMFPClaims({
    customerId,
    skip,
    take,
    sort,
    filters
  }: MFPClaimsGetParams): Observable<ModelPagination<MFPClaim[]>> {
    const sortCol = sort[0]?.field;
    const sortDir = sort[0]?.dir;

    return this.http
      .post<{ total: number; data: MFPClaim[] }>(
        `${environment.api}/customer/${customerId}/get-mfp-claims`,
        {
          skip,
          take,
          sortCol,
          sortDir,
          filters
        }
      )
      .pipe(
        map(({ total, data }) => ({
          skip,
          take,
          total,
          data: data.map(transformMFPClaim)
        }))
      );
  }

  updateMFPClaims({
    customerId,
    idList,
    paymentStatus
  }: MFPClaimsUpdateParams): Observable<any> {
    return this.http.post(
      `${environment.api}/customer/${customerId}/update-mfp-claims`,
      { idList, paymentStatus }
    );
  }
}
